import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, Plus, Search, Edit3, Trash2, EyeOff, Eye, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchPages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`);
      setPages(data || []);
    } catch {
      toast.error("Failed to load pages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter((p) => p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q));
  }, [pages, query]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${slug}`);
      setPages((prev) => prev.filter((p) => p.slug !== slug));
      toast.success("Page deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (p) => {
    try {
      const { data } = await axios.patch(`${API}/admin/pages/${p.slug}`, {
        published: !p.published,
      });
      setPages((prev) => prev.map((x) => (x.slug === p.slug ? { ...x, ...data } : x)));
      toast.success(data.published ? "Published." : "Unpublished.");
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div data-testid="admin-pages-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <FileText className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Custom Pages</h1>
            <span className="text-xs text-slate-400">({pages.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            One-off marketing / legal pages served at <span className="font-mono">/p/&lt;slug&gt;</span> via a free-form rich-text editor. To edit the built-in marketing pages (Solutions, Products, Careers, etc.) use <span className="font-semibold text-slate-700">Site Pages</span> instead.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages…"
              className="pl-9 h-9 w-60 text-xs"
              data-testid="admin-pages-search"
            />
          </div>
          <Button
            onClick={() => navigate("/admin/pages/new")}
            data-testid="admin-pages-new"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New page
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">
              {pages.length === 0 ? "No pages yet. Create your first page." : "No pages match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-pages-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                  <th className="text-left px-4 py-2.5 font-semibold">URL</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Last updated</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.map((p) => (
                  <tr key={p.slug} data-testid={`admin-pages-row-${p.slug}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900 line-clamp-1">{p.title}</p>
                    </td>
                    <td className="px-4 py-3 text-[11px] font-mono text-slate-500">/p/{p.slug}</td>
                    <td className="px-4 py-3">
                      {p.published ? (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Eye className="h-2.5 w-2.5" /> Published
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <EyeOff className="h-2.5 w-2.5" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500">
                      {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {p.published && (
                          <Link
                            to={`/p/${p.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View on site"
                            className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        )}
                        <button
                          onClick={() => handleTogglePublish(p)}
                          data-testid={`admin-pages-toggle-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          title={p.published ? "Unpublish" : "Publish"}
                        >
                          {p.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <Link
                          to={`/admin/pages/${p.slug}`}
                          data-testid={`admin-pages-edit-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(p.slug)}
                          data-testid={`admin-pages-delete-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
